import { Box, Button, Link as MuiLink, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { formatCnpj } from "utils/federal-tax-id";
import { Page } from "v2/components";
import { fetchMonitor, fetchPortfolio, monitorQueryKey } from "v2/pages/Workspace/Activity/AppCampaign/Monitor/monitor.service";
import { CampaignMonitorResponse, MonitorableType } from "v2/pages/Workspace/Activity/AppCampaign/Monitor/monitor.types";
import { destinationErrorCount } from "v2/pages/Workspace/Activity/AppCampaign/Monitor/monitor.utils";
import { CAMPAIGN_TYPE_LABEL } from "v2/services/contractLifecycle.service";
import { errorMessageOf } from "v2/utils/errorMessageOf";
import { parseMonitorable } from "./OrganizationCampaignMonitor";

/**
 * why: cada monitor por CNPJ abre a conta Snowflake da compradora (a BRK tem 23 contas; cerca de
 * US$ 0,10 por abertura). A carteira lista os CNPJs pelo Postgres e só busca o monitor do CNPJ que o
 * usuário abre, e o guarda por 10 minutos.
 */
const PORTFOLIO_MONITOR_STALE_MS = 10 * 60 * 1000;

const unreadCount = (d: CampaignMonitorResponse) => d.contracts.filter((c) => c.unreadable).length;
/**
 * why: admin, super usuário e Conducco passam pela guarda da carteira de qualquer vendedora, mas o monitor
 * por CNPJ lhes dá todos os contratos do comprador, inclusive os de outra corretora (F3 da revisão).
 */
const foreignCount = (d: CampaignMonitorResponse, workspaceIds: string[]) =>
  d.contracts.filter((c) => !workspaceIds.includes(c.workspaceId)).length;

function Caveats({ items }: { items: (string | null)[] }) {
  return (
    <>
      {items
        .filter((c): c is string => !!c)
        .map((c) => (
          <Typography key={c} variant="body2" color="warning.main">
            {c}
          </Typography>
        ))}
    </>
  );
}

function MonitorCells({ data, workspaceIds }: { data: CampaignMonitorResponse; workspaceIds: string[] }) {
  const unread = unreadCount(data);
  const allUnread = unread > 0 && unread === data.contracts.length;
  const scope = unread ? " nos contratos lidos" : "";
  const { titulares, waiting, oldestWaitingDays } = data.top;
  const destinationErrors = destinationErrorCount(data);
  const foreign = foreignCount(data, workspaceIds);

  return (
    <>
      <TableCell>{allUnread ? "dados não lidos" : `${titulares.resolved} de ${titulares.total} (${titulares.percent}%)${scope}`}</TableCell>
      {/* invariant: com contrato não lido, zero aguardando seria mentira; diz "dados não lidos". */}
      <TableCell>{allUnread || (unread && waiting === 0) ? "dados não lidos" : `${waiting}${scope}`}</TableCell>
      <TableCell>{allUnread ? "dados não lidos" : oldestWaitingDays != null ? `${oldestWaitingDays} dias${scope}` : "-"}</TableCell>
      <TableCell>
        <Caveats
          items={[
            unread ? `Dados de ${unread} contrato(s) não lidos` : null,
            destinationErrors ? `Destino não lido para ${destinationErrors} titular(es)` : null,
            foreign ? `Inclui ${foreign} contrato(s) de outra corretora` : null,
          ]}
        />
      </TableCell>
    </>
  );
}

type LoadedEntry = { data: CampaignMonitorResponse; workspaceIds: string[] };

function PortfolioTotal({ entries }: { entries: LoadedEntry[] }) {
  // invariant: CNPJ com contrato de outra corretora fica fora da soma; o topo dele não separa os contratos.
  const included = entries.filter((e) => foreignCount(e.data, e.workspaceIds) === 0).map((e) => e.data);
  const excluded = entries.length - included.length;
  const sum = (f: (d: CampaignMonitorResponse) => number) => included.reduce((n, d) => n + f(d), 0);
  const resolved = sum((d) => d.top.titulares.resolved);
  const total = sum((d) => d.top.titulares.total);
  const waiting = sum((d) => d.top.waiting);
  const unread = sum(unreadCount);
  const destinationErrors = sum(destinationErrorCount);
  // invariant: a mesma guarda da linha; com contrato não lido, nunca "0 aguardando".
  const waitingText = unread && waiting === 0 ? "aguardando: dados não lidos" : `${waiting} aguardando`;
  const main = unread
    ? `Carteira, nos contratos lidos: ${resolved} de ${total} titulares resolvidos · ${waitingText}; dados de ${unread} contrato(s) não lidos`
    : `Carteira: ${resolved} de ${total} titulares resolvidos · ${waitingText}`;
  const text = excluded ? `${main}; fora da soma: ${excluded} CNPJ(s) com contratos de outra corretora` : main;
  return (
    <Box>
      <Typography variant="h6">{text}</Typography>
      <Caveats items={[destinationErrors ? `Na carteira: destino não lido para ${destinationErrors} titular(es)` : null]} />
    </Box>
  );
}

function Portfolio({ sellerOrganizationId, type }: { sellerOrganizationId: string; type: MonitorableType }) {
  const [opened, setOpened] = useState<string[]>([]);
  const portfolio = useQuery({
    queryKey: ["campaign-portfolio", sellerOrganizationId, type],
    queryFn: () => fetchPortfolio(sellerOrganizationId, type),
  });
  const orgs = portfolio.data?.organizations ?? [];
  const monitors = useQueries({
    queries: orgs.map((o) => {
      const source = { kind: "ORGANIZATION" as const, organizationId: o.organizationId };
      return {
        queryKey: monitorQueryKey(source, type),
        queryFn: () => fetchMonitor(source, type),
        enabled: opened.includes(o.organizationId),
        staleTime: PORTFOLIO_MONITOR_STALE_MS,
        refetchOnWindowFocus: false,
      };
    }),
  });

  if (portfolio.isPending) return <Typography>Carregando a carteira...</Typography>;
  if (portfolio.isError) return <Typography color="error">{errorMessageOf(portfolio.error, "Não foi possível carregar a carteira.")}</Typography>;
  if (orgs.length === 0) return <Typography>Nenhum CNPJ nesta carteira.</Typography>;

  // invariant: só os CNPJs abertos aqui; a consulta desabilitada devolve o cache de outra tela (atalho do monitor por CNPJ).
  const openedIdx = orgs.flatMap((o, i) => (opened.includes(o.organizationId) ? [i] : []));
  const loaded = openedIdx.flatMap((i): LoadedEntry[] => {
    const data = monitors[i].data;
    return data ? [{ data, workspaceIds: orgs[i].workspaceIds }] : [];
  });
  const failed = openedIdx.filter((i) => monitors[i].isError).length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {loaded.length === orgs.length ? (
        <PortfolioTotal entries={loaded} />
      ) : failed ? (
        <Typography variant="body2" color="error">{`Soma da carteira indisponível: monitor de ${failed} CNPJ(s) não lido(s).`}</Typography>
      ) : (
        <Typography variant="body2">Abra os CNPJs para ver os números; a soma da carteira aparece com todos abertos.</Typography>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Empresa</TableCell>
            <TableCell>Contratos</TableCell>
            <TableCell>Titulares resolvidos</TableCell>
            <TableCell>Aguardando</TableCell>
            <TableCell>Mais antiga</TableCell>
            <TableCell>Ressalvas</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orgs.map((o, i) => {
            const m = monitors[i];
            const name = o.corporateName ?? (o.federalTaxId ? formatCnpj(o.federalTaxId) : o.organizationId);
            return (
              <TableRow key={o.organizationId}>
                <TableCell>
                  <MuiLink component={Link} to={`/v2/organizations/${o.organizationId}/campanhas/${type}`}>
                    {name}
                  </MuiLink>
                </TableCell>
                <TableCell>{o.workspaceIds.length}</TableCell>
                {!opened.includes(o.organizationId) ? (
                  <TableCell colSpan={4}>
                    <Button size="small" aria-label={`Ver números de ${name}`} onClick={() => setOpened((cur) => [...cur, o.organizationId])}>
                      Ver números
                    </Button>
                  </TableCell>
                ) : m.data ? (
                  <MonitorCells data={m.data} workspaceIds={o.workspaceIds} />
                ) : m.isError ? (
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="error">
                      {errorMessageOf(m.error, "Não foi possível ler o monitor deste CNPJ.")}
                    </Typography>
                  </TableCell>
                ) : (
                  <TableCell colSpan={4}>Carregando...</TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

/** invariant: carteira só da própria vendedora (R13); o backend recusa com 404 qualquer outra. */
export function SellerCampaignPortfolio() {
  const { sellerOrganizationId, type } = useParams() as { sellerOrganizationId: string; type: string };
  const monitorable = parseMonitorable(type);

  if (!monitorable) {
    return (
      <Page title="Campanhas">
        <Page.Tab name="Carteira">
          <Box>Tipo de campanha sem monitor.</Box>
        </Page.Tab>
      </Page>
    );
  }
  return (
    <Page title={`Carteira: ${CAMPAIGN_TYPE_LABEL[monitorable]}`}>
      <Page.Tab name="Carteira">
        <Portfolio key={`${sellerOrganizationId}|${monitorable}`} sellerOrganizationId={sellerOrganizationId} type={monitorable} />
      </Page.Tab>
    </Page>
  );
}
